<template>
  <StatusCircle
    :model-value="action.status"
    :disable="!showPopup"
    :loading="loading"
    :show-act-and-archive="showActAndArchive"
    :show-only-archive-text="action.status === TaskStatus.act"
    :disable-act-and-archive-reason="disableActAndArchiveReason"
    @update:model-value="updateActionStatus"
    @archive-event="updateStatusAndArchiveEvent"
  >
    <template #default="{ resetTimer, stopTimer }">
      <slot :reset-timer="resetTimer" :stop-timer="stopTimer"></slot>
    </template>
  </StatusCircle>
</template>

<script setup lang="ts">
import { type PropType, computed, inject } from "vue";
import { DateTime } from "luxon";
import { useStore } from "vuex";
import {
  ActionType,
  EventType,
  PreventArchivingType,
  TaskStatus,
  useArchiveEventMutation,
  useUpdateActionStatusMutation,
  type ActionListActionFieldsFragment,
} from "@/graphql";
import StatusCircle from "./StatusCircle.vue";
import {
  BoardIdInjectionKey,
  EABEventInjectionKey,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import { useEventArchive } from "../event/composables/eventArchive";
import type { ActionEditType } from "../task/helper/DefaultTask";

const props = defineProps({
  action: {
    type: Object as PropType<ActionEditType | ActionListActionFieldsFragment>,
    required: true,
  },
  disablePopup: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { mutate: mutateActionStatus, loading } = useUpdateActionStatusMutation({});

const event = inject(EABEventInjectionKey);
const boardId = inject(BoardIdInjectionKey);

const { archivingBlockedReason, archivingBlockedEnum, blockingTask } = useEventArchive(
  computed(() => ({
    actions: event?.value?.actions ?? [],
    preventsArchiving: event?.value?.preventsArchiving,
  })),
);

const showActAndArchive = computed(() => event != null);
const disableActAndArchiveReason = computed(() => {
  return archivingBlockedEnum.value != null &&
    !(
      archivingBlockedEnum.value === PreventArchivingType.ACTION_STATUS &&
      blockingTask.value.length === 1 &&
      blockingTask.value[0].id === props.action.id
    )
    ? archivingBlockedReason.value
    : undefined;
});

async function updateStatusAndArchiveEvent() {
  if (!showPopup.value) return;
  await updateActionStatus(TaskStatus.act);
  if (event == null) {
    return;
  }
  await archiveEvent(
    {
      id: event.value.id,
      eventType: event.value.__typename as EventType,
      boardId: boardId?.value ?? undefined,
    },
    {
      optimisticResponse: {
        archiveEvent: {
          __typename: event.value.__typename,
          id: event.value.id,
          deletedAt: DateTime.now().toISO(),
          updatedAt: DateTime.now().toISO(),
        },
      },
    },
  );
}

const { mutate: archiveEvent } = useArchiveEventMutation();

const updateActionStatus = async (status: number) => {
  if (!showPopup.value) return;
  const commentatorId = store.getters["taskComment/getTaskCommentatorId"];
  const revisions = "Revisions" in props.action ? { Revisions: props.action.Revisions } : {};
  if (props.action.id == null) {
    return;
  }
  await mutateActionStatus(
    {
      actionId: props.action.id,
      actionType: props.action.__typename as ActionType,
      status,
      userId: commentatorId,
      boardId: boardId?.value ?? undefined,
    },
    {
      optimisticResponse: {
        UpdateActionStatus: {
          __typename: props.action.__typename,
          id: props.action.id,
          updatedAt: DateTime.now().toISO(),
          status,
          preventsArchiving: status !== TaskStatus.act,
          deletedAt: props.action.deletedAt,
          ...revisions,
        },
      },
    },
  );
};

const showPopup = computed(() => props.action.deletedAt === null && !props.disablePopup);
</script>

<style lang="scss" scoped></style>
